package com.eladfinish.bible.contest.screens.settings.debug

import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsKeys
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetPromptGate
import com.eladfinish.bible.contest.screens.americanQuestions.AmericanQuestionsStore
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabsStore
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseStore
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.PastQuizzesSheetGate
import com.eladfinish.bible.contest.screens.main.verseWidgetSheet.VerseWidgetSheetGate
import com.eladfinish.bible.contest.screens.mikud.MikudStore
import com.eladfinish.bible.contest.screens.openQuestions.OpenQuestionsStore
import com.eladfinish.bible.contest.screens.pyramidCrossword.PyramidCrosswordStore
import com.eladfinish.bible.contest.screens.search.SearchStore

/** A boolean kept in Settings, with the default the app assumes while it was never written. */
data class DebugFlag(
    val key: String,
    val defaultValue: Boolean,
    val group: DebugFlagGroup,
)

enum class DebugFlagGroup(val title: String) {
    ONE_TIME("הודעות ועזרה חד-פעמיות"),
    SEARCH_AND_COPY("חיפוש והעתקה"),
    TEXT_DISPLAY("תצוגת הטקסט"),
    GAMES("משחקים ותרגול"),
    GENERAL("כללי"),
    PURCHASES("רכישות"),
}

/**
 * Every boolean the app keeps in Settings, for the debug dashboard. Storage can't list them
 * itself (a flag that was never written isn't there, and iOS/web don't keep the type), so a new
 * boolean flag has to be added here to show up. Keys always come from the constant the app
 * itself reads with; defaults mirror that read. The order inside a group doesn't matter: the
 * screen lists each group alphabetically by key ([DebugFlagsRepository.states]).
 */
val debugFlags: List<DebugFlag> = buildList {
    fun flag(key: String, defaultValue: Boolean, group: DebugFlagGroup) {
        add(DebugFlag(key = key, defaultValue = defaultValue, group = group))
    }

    DebugFlagGroup.ONE_TIME.let { group ->
        flag(Constants.PREF_SEARCH_HISTORY_HINT_SHOWN, false, group)
        flag(Constants.PREF_HEBREW_GENDER_LANGUAGE_SET, false, group)
        // "what's new" sheet of חידונים אמיתיים: shown off + existing user on = it shows again
        flag(PastQuizzesSheetGate.KEY_SHOWN, false, group)
        flag(PastQuizzesSheetGate.KEY_EXISTING_USER, false, group)
        // same for the sheet of ווידג'ט פסוק מתחלף
        flag(VerseWidgetSheetGate.KEY_SHOWN, false, group)
        flag(VerseWidgetSheetGate.KEY_EXISTING_USER, false, group)
        // the hint on the random-verse screen that offers the widget: done on = it never shows,
        // off = it may show again (its counters, the number of times and the date, are not booleans)
        flag(VerseWidgetPromptGate.KEY_DONE, false, group)
        flag(SearchStore.SKIP_READ_ME_PREF, false, group)
        flag(CompleteVerseStore.SKIP_READ_ME_PREF, false, group)
        flag(AmericanQuestionsStore.SKIP_READ_ME_PREF, false, group)
        flag(OpenQuestionsStore.SKIP_README_PREF, true, group)
        flag(MikudStore.SKIP_READ_ME_PREF, false, group)
        flag(BrowseTabsStore.SKIP_READ_ME_PREF, false, group)
        flag(PyramidCrosswordStore.SKIP_READ_ME_PREF, false, group)
    }

    DebugFlagGroup.SEARCH_AND_COPY.let { group ->
        flag(CopyOptionsKeys.INCLUDE_NIKUD, true, group)
        flag(CopyOptionsKeys.INCLUDE_TEAMIM, true, group)
        flag(CopyOptionsKeys.HANDLE_GOD_NAMES, true, group)
        flag(CopyOptionsKeys.INCLUDE_BOOK_AND_CHAPTER, true, group)
        flag(CopyOptionsKeys.INCLUDE_EXPRESSION, true, group)
        flag(CopyOptionsKeys.SORT_RESULTS, false, group)
        flag(CopyOptionsKeys.REMOVE_VERSE_ORIGIN, false, group)
        flag(CopyOptionsKeys.REMOVE_DUPLICATES, false, group)
        flag(CopyOptionsKeys.COPY_ONLY_HIGHLIGHTS, false, group)
    }

    DebugFlagGroup.TEXT_DISPLAY.let { group ->
        flag(SharedPrefsConstants.ONLY_QRI_SWITCH, true, group)
        flag(Constants.PREF_NIKUD_SWITCH, true, group)
        flag(Constants.PREF_SHOW_WITH_TEAMIM, BooleanConstants.ENABLE_SHOW_WITH_TEAMIM_PREF, group)
        flag(SharedPrefsConstants.HIDE_GOD_NAME, BooleanConstants.ENABLE_HIDE_GOD_NAME, group)
        flag(SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE, BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE, group)
        flag(SharedPrefsConstants.KEY_REMOVE_VERSE_LETTER, false, group)
        flag(SharedPrefsConstants.KEY_HIDE_TOOLBAR_ON_SCROLL, BooleanConstants.ENABLE_HIDE_TOOLBAR_ON_SCROLL, group)
        flag(Constants.PREF_BOOKMARK_ONQELOS, false, group)
    }

    DebugFlagGroup.GAMES.let { group ->
        flag(Constants.PREF_HIDE_PEOPLE_NAMES_SWITCH, true, group)
        flag(Constants.PREF_SHOW_FULL_CHAPTER_SWITCH, true, group)
        flag(Constants.PREF_ALPHABETICAL_SWITCH, false, group)
        flag(SharedPrefsConstants.KEY_ENABLE_HALF_VERSE, BooleanConstants.ENABLE_HALF_VERSE_DEFAULT, group)
        flag(SharedPrefsConstants.KEY_ENABLE_CHAPTER_IDENTIFYING_TASK, BooleanConstants.ENABLE_CHAPTER_IDENTIFYING_TASK, group)
        flag(SharedPrefsConstants.KEY_ENABLE_PARASHA_IDENTIFYING_TASK, BooleanConstants.ENABLE_PARASHA_IDENTIFYING_TASK, group)
        flag(SharedPrefsConstants.KEY_ENABLE_LINE_LENGTH_ACCORDING_TO_WORD_LENGTH, true, group)
        flag(SharedPrefsConstants.KEY_ENABLE_SPACES_BETWEEN_BLANKS_IN_CHAPTER_COMPLETE, true, group)
        flag(SharedPrefsConstants.KEY_PAIR_MATCHING_SORTABLE_LIST, BooleanConstants.ENABLE_PAIR_MATCHING_SORTABLE_LIST, group)
        flag(SharedPrefsConstants.PAIR_MATCHING_ALPHABETICAL_ORDER, BooleanConstants.ENABLE_PAIR_MATCHING_ALPHABETICAL_ORDER, group)
        flag(SharedPrefsConstants.KEY_PAIR_MATCHING_DRAG_ON_LONG_CLICK, BooleanConstants.ENABLE_PAIR_MATCHING_LONG_CLICK, group)
        flag(SharedPrefsConstants.OPEN_QUESTIONS_SHUFFLE_SWITCH, true, group)
        flag(Constants.PREF_ENABLE_COUNTDOWN_ON_AMERICAN_QUESTIONS, true, group)
        flag(Constants.PREF_MIKUD_FILTER_SWITCH, true, group)
    }

    DebugFlagGroup.GENERAL.let { group ->
        flag(Constants.PREF_SOUND_SWITCH, true, group)
        flag(Constants.PREF_VIBRATION_SWITCH, true, group)
        flag(Constants.PREF_ENABLE_SHAKE_SWITCH, true, group)
        flag(Constants.PREF_ENABLE_ANONYMOUS_USAGE_STATS, BooleanConstants.ENABLE_ANONYMOUS_USAGE_STATS, group)
        flag(Constants.PREF_SAVE_PDF_EMAIL_DETAILS, true, group)
        flag(Constants.RESET_MIKUD_ON_UPDATE, true, group)
        flag(SharedPrefsConstants.REVIEW_DEBUG_FORCE, false, group)
        // the widget hint shows at the next verse whatever the user's history says (debug builds only)
        flag(VerseWidgetPromptGate.KEY_DEBUG_FORCE, false, group)
    }

    DebugFlagGroup.PURCHASES.let { group ->
        flag(SharedPrefsConstants.AD_FREE_PURCHASED, false, group)
        flag(Constants.IS_PREMIUM, false, group)
    }
}
